import Keycloak, { type KeycloakInitOptions } from "keycloak-js";
import { env } from "@/lib/env";

export const keycloak = new Keycloak({
  url: env.VITE_KEYCLOAK_URL,
  realm: env.VITE_KEYCLOAK_REALM,
  clientId: env.VITE_KEYCLOAK_CLIENT_ID,
});

export const KEYCLOAK_INIT: KeycloakInitOptions = {
  onLoad: "check-sso",
  pkceMethod: "S256",
  silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  checkLoginIframe: false,
};

/**
 * Singleton init: keycloak-js permette una sola init() per istanza.
 * In React 19 StrictMode l'effect viene eseguito 2x in dev, quindi
 * memoizziamo la promise per evitare la seconda init().
 */
let initPromise: Promise<boolean> | undefined;

export function initKeycloak(): Promise<boolean> {
  if (!initPromise) {
    initPromise = keycloak.init(KEYCLOAK_INIT);
  }
  return initPromise;
}

const REFRESH_INTERVAL_MS = 50_000;
const MIN_VALIDITY_SECONDS = 60;

let refreshTimer: ReturnType<typeof setInterval> | undefined;

export function startTokenRefreshLoop(): void {
  stopTokenRefreshLoop();
  refreshTimer = setInterval(() => {
    keycloak.updateToken(MIN_VALIDITY_SECONDS).catch((err) => {
      console.error("[keycloak] token refresh failed", err);
    });
  }, REFRESH_INTERVAL_MS);
}

export function stopTokenRefreshLoop(): void {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
}

export async function ensureFreshToken(minValidity = 30): Promise<string | undefined> {
  try {
    await keycloak.updateToken(minValidity);
    return keycloak.token;
  } catch {
    return undefined;
  }
}

export function getAccessToken(): string | undefined {
  return keycloak.token;
}

function hasRealmRole(role: string): boolean {
  const roles = keycloak.tokenParsed?.realm_access?.roles;
  return Array.isArray(roles) && roles.includes(role);
}

/**
 * True when the user has the realm-level role `herika-admin`.
 *
 * D14 area role for the company-admin UI (/admin/*). NOTE: this is NO LONGER the
 * cross-org bypass — that moved to `herika-platform` (see `isHerikaPlatform`).
 */
export function isHerikaAdmin(): boolean {
  return hasRealmRole("herika-admin");
}

/**
 * True when the user has the realm-level role `herika-platform`.
 *
 * D14 platform role (Henoia): cross-org views (/platform/*) and the feature-flag
 * console. This is the realm role the booking-engine honours for its ownership
 * bypass.
 */
export function isHerikaPlatform(): boolean {
  return hasRealmRole("herika-platform");
}

/** True when the user has the realm-level role `herika-dev` (developer area). */
export function isHerikaDev(): boolean {
  return hasRealmRole("herika-dev");
}

/** True when the user has the realm-level role `herika-crm` (CRM area). */
export function isHerikaCrm(): boolean {
  return hasRealmRole("herika-crm");
}

export function logout(): void {
  // Clear redux-persist before logging out. Without this, the next user
  // (possibly with a different active role / org) inherits the previous
  // session's `profile` slice, which makes `X-Active-Role` header point at
  // an organization the new user has no membership in — resulting in a
  // 403 loop that the bootstrap can't recover from.
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("persist:"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // best effort; never block logout because of storage errors
  }
  void keycloak.logout({ redirectUri: window.location.origin });
}

export function login(): void {
  void keycloak.login({ redirectUri: window.location.href });
}
